import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App as AntApp, ConfigProvider } from 'antd';
import ruRU from 'antd/locale/ru_RU';
import { lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { LoginPage } from './auth/LoginPage';
import { RequireAuth, RequirePermission } from './auth/guards';
import './i18n';
import { AppLayout } from './layout/AppLayout';

// Route-level code splitting: each page loads when it is first visited, so the
// initial bundle carries the layout + login, not every report and editor.
const lazyPage = <T extends object>(
  load: () => Promise<T>,
  pick: (m: T) => React.ComponentType,
): React.LazyExoticComponent<React.ComponentType> =>
  lazy(() => load().then((m) => ({ default: pick(m) })));

const AuditPage = lazyPage(() => import('./features/audit/AuditPage'), (m) => m.AuditPage);
const CatalogPage = lazyPage(() => import('./features/catalog/CatalogPage'), (m) => m.CatalogPage);
const ClientInfoPage = lazyPage(
  () => import('./features/clients/ClientInfoPage'),
  (m) => m.ClientInfoPage,
);
const ClientsPage = lazyPage(() => import('./features/clients/ClientsPage'), (m) => m.ClientsPage);
const LabelsPage = lazyPage(() => import('./features/labels/LabelsPage'), (m) => m.LabelsPage);
const OrdersPage = lazyPage(() => import('./features/orders/OrdersPage'), (m) => m.OrdersPage);
const ReportsPage = lazyPage(() => import('./features/reports/ReportsPage'), (m) => m.ReportsPage);
const SettingsPage = lazyPage(
  () => import('./features/settings/SettingsPage'),
  (m) => m.SettingsPage,
);
const ShipmentsPage = lazyPage(
  () => import('./features/shipments/ShipmentsPage'),
  (m) => m.ShipmentsPage,
);
const StaffPage = lazyPage(() => import('./features/staff/StaffPage'), (m) => m.StaffPage);
const WarehouseMapPage = lazyPage(
  () => import('./features/warehouse/WarehouseMapPage'),
  (m) => m.WarehouseMapPage,
);
const SyncPage = lazyPage(() => import('./features/sync/SyncPage'), (m) => m.SyncPage);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Admin data is edited by several people at once; a stale table that never
      // refetches would show one admin the world as another admin left it.
      staleTime: 10_000,
      retry: 1,
      refetchOnWindowFocus: true,
    },
  },
});

export function App() {
  return (
    <ConfigProvider locale={ruRU} theme={{ token: { colorPrimary: '#0b7285', borderRadius: 6 } }}>
      <AntApp>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginPage />} />

              <Route
                element={
                  <RequireAuth>
                    <AppLayout />
                  </RequireAuth>
                }
              >
                <Route index element={<Navigate to="/clients" replace />} />
                <Route
                  path="/clients"
                  element={
                    <RequirePermission permission="clients:read">
                      <ClientsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/clients/:id"
                  element={
                    <RequirePermission permission="clients:read">
                      <ClientInfoPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/staff"
                  element={
                    <RequirePermission permission="staff:manage">
                      <StaffPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/orders"
                  element={
                    <RequirePermission permission="orders:read:all">
                      <OrdersPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/shipments"
                  element={
                    <RequirePermission permission="shipments:read:all">
                      <ShipmentsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/catalog"
                  element={
                    <RequirePermission permission="catalog:read:admin">
                      <CatalogPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/warehouse-map"
                  element={
                    <RequirePermission permission="catalog:read:admin">
                      <WarehouseMapPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/labels"
                  element={
                    <RequirePermission permission="catalog:read:admin">
                      <LabelsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <RequirePermission permission="reports:view">
                      <ReportsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/sync"
                  element={
                    <RequirePermission permission="sync:manage">
                      <SyncPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <RequirePermission permission="settings:manage">
                      <SettingsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/audit"
                  element={
                    <RequirePermission permission="audit:read">
                      <AuditPage />
                    </RequirePermission>
                  }
                />
              </Route>

              <Route path="*" element={<Navigate to="/clients" replace />} />
            </Routes>
          </BrowserRouter>
        </QueryClientProvider>
      </AntApp>
    </ConfigProvider>
  );
}
